import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmt = (v) => `£${Number(v || 0).toFixed(2)}`;

function StatusBadge({ status }) {
  if (status === 'paid')    return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Paid</span>;
  if (status === 'overdue') return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Overdue</span>;
  return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending</span>;
}

const PaymentHistoryView = ({ memberId, subscription, onPaymentMarked }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeSub, setActiveSub] = useState(null);
  const [markingMonth, setMarkingMonth] = useState(null);
  const [markForm, setMarkForm] = useState({ paymentMethod: '', amount: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonthIdx = new Date().getMonth();

  const joinDate = new Date(subscription?.joinDate || subscription?.join_date || 0);
  const joinYear = isNaN(joinDate.getFullYear()) ? 2020 : joinDate.getFullYear();
  const years = Array.from({ length: currentYear - Math.max(joinYear, 2026) + 1 }, (_, i) => Math.max(joinYear, 2026) + i);

  const fetchPayments = async () => {
    try {
      const data = await apiService.getSubscriptionPayments(memberId);
      setPayments(data);
      const subs = await apiService.getSubscriptions();
      const sub = subs.find(s => String(s.member_id) === String(memberId) && s.status === 'active');
      setActiveSub(sub || null);
      if (sub) setMarkForm(f => ({ ...f, amount: sub.amount || subscription?.contribution || '' }));
    } catch (e) {
      console.error('Error fetching payments:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [memberId]); // eslint-disable-line

  const getMonthPayment = (monthIdx) => payments.find(p => {
    const d = new Date(p.payment_month);
    return d.getMonth() === monthIdx && d.getFullYear() === selectedYear;
  });

  const getStatus = (monthIdx) => {
    const p = getMonthPayment(monthIdx);
    if (p?.status === 'paid') return 'paid';
    const isStartMonth = selectedYear === joinYear && monthIdx < joinDate.getMonth();
    if (isStartMonth) return 'na';
    const inFuture = selectedYear > currentYear || (selectedYear === currentYear && monthIdx > currentMonthIdx);
    if (inFuture) return 'future';
    return 'overdue';
  };

  const handleMarkPaid = async () => {
    if (!activeSub) { alert('No active subscription found.'); return; }
    if (!markForm.paymentMethod) { alert('Please select a payment method.'); return; }
    setSaving(true);
    try {
      await apiService.markPayment(activeSub.id, {
        month: markingMonth + 1, year: selectedYear,
        paymentMethod: markForm.paymentMethod,
        notes: markForm.notes,
        amount: markForm.amount,
      });
      await fetchPayments();
      setMarkingMonth(null);
      setMarkForm({ paymentMethod: '', amount: activeSub?.amount || '', notes: '' });
      if (onPaymentMarked) onPaymentMarked();
    } catch (e) {
      console.error('Error marking payment:', e);
      alert('Failed to mark payment.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="text-center py-10">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
      <p className="text-slate-500 text-sm">Loading payment history…</p>
    </div>
  );

  const yearPayments = payments.filter(p => new Date(p.payment_month).getFullYear() === selectedYear);
  const paidTotal = yearPayments.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const paidCount = yearPayments.filter(p => p.status === 'paid').length;

  return (
    <div className="space-y-4">
      {/* Year selector + summary */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => { setSelectedYear(y => Math.max(Math.max(joinYear, 2026), y - 1)); setMarkingMonth(null); }}
            disabled={selectedYear <= Math.max(joinYear, 2026)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-slate-800 font-semibold text-sm w-16 text-center">{selectedYear}</span>
          <button onClick={() => { setSelectedYear(y => Math.min(currentYear, y + 1)); setMarkingMonth(null); }}
            disabled={selectedYear >= currentYear}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-4 text-sm">
          <span className="text-slate-500">Paid: <strong className="text-green-600">{paidCount}</strong></span>
          <span className="text-slate-500">Collected: <strong className="text-blue-600">{fmt(paidTotal)}</strong></span>
          {activeSub && <span className="text-slate-500">Rate: <strong className="text-slate-700">{fmt(activeSub.amount)}/mo</strong></span>}
        </div>
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {MONTHS.map((month, idx) => {
          const status = getStatus(idx);
          const payment = getMonthPayment(idx);
          const isActive = markingMonth === idx;
          const canMark = (status === 'overdue') && activeSub;

          if (status === 'na') return (
            <div key={month} className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-3 text-center opacity-40">
              <p className="text-slate-400 text-xs">{SHORT[idx]}</p>
            </div>
          );

          return (
            <div key={month} className={`rounded-xl border p-3 transition-all ${
              isActive ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50' :
              status === 'paid' ? 'bg-green-50 border-green-200' :
              status === 'future' ? 'bg-white border-dashed border-slate-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-600">{SHORT[idx]}</span>
                {status === 'paid' && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                {status === 'overdue' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                {status === 'future' && <Clock className="w-3.5 h-3.5 text-slate-300" />}
              </div>
              {status === 'paid' ? (
                <>
                  <p className="text-green-700 font-semibold text-sm">{fmt(payment.amount)}</p>
                  <p className="text-green-500 text-xs">{payment.paid_date ? new Date(payment.paid_date).toLocaleDateString('en-GB', {day:'2-digit',month:'2-digit'}) : ''}</p>
                </>
              ) : status === 'future' ? (
                <p className="text-slate-300 text-xs mt-1">—</p>
              ) : (
                <>
                  <p className="text-red-600 font-semibold text-sm">{fmt(activeSub?.amount || subscription?.contribution)}</p>
                  {canMark && (
                    <button onClick={() => { setMarkingMonth(isActive ? null : idx); }}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                      {isActive ? 'Cancel' : 'Mark paid'}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Mark payment form */}
      {markingMonth !== null && (
        <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
          <h4 className="font-semibold text-slate-800 mb-3 text-sm">
            Mark {MONTHS[markingMonth]} {selectedYear} as Paid
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Payment Method *</label>
              <select value={markForm.paymentMethod}
                onChange={e => setMarkForm(f => ({ ...f, paymentMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">Select method</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="standing_order">Standing Order</option>
                <option value="direct_debit">Direct Debit</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Amount (£)</label>
              <input type="number" value={markForm.amount}
                onChange={e => setMarkForm(f => ({ ...f, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.01" min="0" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input value={markForm.notes}
                onChange={e => setMarkForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Optional" />
            </div>
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <button onClick={() => setMarkingMonth(null)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button onClick={handleMarkPaid} disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving…' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryView;
